import {Textarea} from "@heroui/input";
import {Card, CardBody, CardHeader, Checkbox, DatePicker, Modal, ModalContent, ModalHeader, SelectItem} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {addToast} from "@heroui/toast";
import {parseDate} from "@internationalized/date";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute, useParams} from '@tanstack/react-router'
import dayjs from "dayjs";
import React, {useState} from "react";
import CourtsAvailabilityView from "../../../../components/CourtsAvailabilityView.tsx";
import MapBox from "../../../../components/MapBox.tsx";
import PButton from "../../../../components/PButton.tsx";
import PInput from "../../../../components/PInput.tsx";
import PSelect from "../../../../components/PSelect.tsx";
import Header from "../../../../Header.tsx";
import {useJwtAuth, useJwtUser} from '../../../../JWTAuthProvider.tsx';
import services from "../../../../services";
import {CourtAvailability} from "../../../../types";
import {AMENITIES_LIST, OPENING_DAYS, SPORTS_LIST} from "../../../../user/_components/useEntityEditor.tsx";
import {maybeUndefinedDefaultKey} from "../../../../utilities.ts";
import queryKeys from "../../../../utilities/queryKeys.ts";

export const Route = createFileRoute('/_app/clubs/$club_uid/courts-booking')({
    component: RouteComponent,
})

function CurrencySymbol({code}: { code: string }) {
    const currencySymbols = {
        USD: '$',
        EUR: '€',
        GBP: '£',
        AED: 'AED ',
    }
    return <span>{currencySymbols[code]}</span>
}

function RouteComponent() {
    const auth = useJwtAuth();
    const user = useJwtUser();
    const [sport, setSport] = useState('');
    const [startDate, setStartDate] = useState(parseDate(dayjs().format('YYYY-MM-DD')) as any);
    const queryClient = useQueryClient();
    const params = useParams({strict: false});

    const bookPopupDefaults = {
        open:              false,
        availabilityCourt: null as CourtAvailability | null,
        timeStart:         null as number | null,
        timeStartStr:      null as string | null,
        selectedMinutes:   0,
        is_custom:         false,
        message:           '',
        phone_number:      user?.phone_number,
        acceptedTerms:     false,
        showTerms:         false,
        price:             null as number | null,
        currencyCode:      null as string | null,
    };

    const [bookPopup, setBookPopup] = useState(bookPopupDefaults);

    const courtsQuery = useQuery({
        queryKey: queryKeys.booking.getAvailableCourts({club_uid: params.club_uid, startDate}),
        queryFn:  async () => services.booking.getAvailableCourts({
            club_uid:   params.club_uid,
            start_date: startDate,
        }),
        enabled:  !!user?.uid,
    })

    const bookCourt = useMutation({
        mutationFn: async () => services.booking.book({
            court_uid:        bookPopup!.availabilityCourt!.court!.uid,
            start_date:       startDate,
            start_time:       bookPopup.timeStartStr,
            duration_minutes: bookPopup.selectedMinutes,
            is_custom:        bookPopup.is_custom,
            message:          bookPopup.message,
            phone_number:     bookPopup.phone_number,
            price:            bookPopup.price,
            currencyCode:     bookPopup.currencyCode,
        }),
        onSuccess() {
            addToast({
                color: "success",
                title: "Request has been sent, please wait for confirmation",
            })
            if (!user?.phone_number) {
                auth.reloadUser();
            }
            queryClient.invalidateQueries({queryKey: queryKeys.booking.getAvailableCourts()});
            setBookPopup({...bookPopupDefaults});
        }
    })

    if (!user) {
        return <>
            <Header />
            <div className="text-center"><Spinner /></div>
        </>
    }

    return <>
        <Header backUrl={`/profiles/${user?.uid}`} />

        <div className={"max-w-6xl w-11/12 mx-auto"}>
            <Card>
                <CardHeader>
                    <div className={"w-full flex md:flex-col md:items-start gap-2 justify-between items-center"}>
                        <div>
                            <span className={"font-bold text-xl"}>Book a court in {courtsQuery.data?.club.name}</span><br />
                            <span className={"text-small"}>{courtsQuery.data?.club.address}</span>
                        </div>

                        <div className={"flex gap-2"}>
                            <PSelect
                                placeholder={''}
                                labelPlacement={"inside"}
                                defaultSelectedKeys={maybeUndefinedDefaultKey(sport)}
                                label={"Sport"}
                                className={"min-w-32"}
                                classNames={{label: "w-f1ull", helperWrapper: "hidden!"}}
                                onChange={(e) => {
                                    setSport(e.target.value || '');
                                }}
                            >
                                {Object.entries(SPORTS_LIST).map(([key, value]) => (<SelectItem key={key}>{value}</SelectItem>))}
                            </PSelect>

                            <DatePicker className={"float-right flex-0 max-w-64"}
                                        label="Date"
                                        firstDayOfWeek="mon"
                                        value={startDate}
                                        showMonthAndYearPickers={true}
                                        onChange={(value) => setStartDate(value)}
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardBody>
                    {!courtsQuery.data &&
                        <div className="text-center"><Spinner /></div>
                    }
                    {!!courtsQuery.data &&
                        <CourtsAvailabilityView
                            sport={sport}
                            startDate={startDate}
                            onSlotClick={(availability, slot, timeStr) =>
                                setBookPopup({...bookPopup, availabilityCourt: availability, timeStart: slot, timeStartStr: timeStr, open: true})
                            } />
                    }
                </CardBody>
            </Card>

            <div className={"mt-4 grid grid-cols-3 gap-4 md:grid-cols-1"}>
                <Card>
                    <CardHeader className={"font-bold"}>Amenities</CardHeader>
                    <CardBody>
                        <div className={"flex flex-wrap gap-2"}>
                            {(courtsQuery.data?.club.meta.amenities || []).map((text) => <div className={"bg-gray-100 p-1 px-4 rounded-full"}>{AMENITIES_LIST[text]}</div>)}
                        </div>
                    </CardBody>
                </Card>
                <Card>
                    <CardHeader className={"font-bold"}>Location</CardHeader>
                    <CardBody>
                        {!!courtsQuery.data?.club.address_coordinates && <div className={"flex-1 rounded-xl"}>
                            <MapBox coordinates={courtsQuery.data?.club.address_coordinates} />
                        </div>}
                        <div>{courtsQuery.data?.club.address}</div>
                    </CardBody>
                </Card>
                <Card>
                    <CardHeader className={"font-bold"}>Opening hours</CardHeader>
                    <CardBody>
                        {OPENING_DAYS.map((day, dayIndex) => (<div className={"flex-1 flex py-1 items-center border-b-1 border-b-gray-100 justify-between"}>
                            <span>{day}</span>
                            {!courtsQuery.data?.club.opening_hours_per_day?.[dayIndex]?.[0] && <span>-</span>}
                            {courtsQuery.data?.club.opening_hours_per_day?.[dayIndex]?.[0] && <span>{courtsQuery.data?.club.opening_hours_per_day?.[dayIndex]?.[0]} - {courtsQuery.data?.club.opening_hours_per_day?.[dayIndex]?.[1]}</span>}
                        </div>))}
                    </CardBody>
                </Card>
            </div>

            <Modal isOpen={bookPopup.open} placement={"center"} onOpenChange={() => {
                setBookPopup({...bookPopup, open: false});
            }}>
                <ModalContent>
                    {() => (
                        <form onSubmit={e => {
                            e.preventDefault();
                            bookCourt.mutate();
                        }}>
                            <ModalHeader className="flex flex-col gap-1">{bookPopup.availabilityCourt?.court.name}</ModalHeader>

                            <div className={"p-4"}>
                                Booking time: {bookPopup.timeStartStr}<br />
                                Booking duration:<br />
                                <div className={"mt-2 grid grid-cols-2 gap-2"}>
                                    {bookPopup.availabilityCourt?.slots
                                        .filter((slot) => slot.start_time === bookPopup.timeStart)
                                        .map((slot) =>
                                            <Card isPressable={true}
                                                  className={(!bookPopup.is_custom && bookPopup.selectedMinutes === slot.duration_minutes) ? 'bg-secondary' : ''}
                                                  onPress={() => setBookPopup({
                                                      ...bookPopup,
                                                      selectedMinutes: slot.duration_minutes,
                                                      price:           slot.price?.[courtsQuery.data?.club.default_currency_code || 'USD'],
                                                      currencyCode:    courtsQuery.data?.club.default_currency_code || 'USD',
                                                      is_custom:       false
                                                  })}
                                            ><CardBody>
                                                <div className={"flex justify-between"}>
                                                    <span>{slot.duration_minutes}m</span>
                                                    {!!slot.price?.[courtsQuery.data?.club.default_currency_code || 'USD'] && <span><CurrencySymbol code={courtsQuery.data?.club.default_currency_code || 'USD'} />{slot.price?.['USD']}</span>}
                                                </div>
                                            </CardBody></Card>
                                        )}
                                    <Card className={"col-span-2 " + (bookPopup.is_custom ? 'bg-secondary' : '')} isPressable={true}
                                          onPress={() => setBookPopup({
                                              ...bookPopup,
                                              selectedMinutes: 30,
                                              is_custom:       true,
                                              price:           null,
                                              currencyCode:    null,
                                          })}
                                    ><CardBody>Custom</CardBody>
                                    </Card>
                                </div>
                                {bookPopup.is_custom && <div>
                                    <Textarea
                                        className="mt-3!"
                                        label={"Booking details"}
                                        placeholder={"Please describe your request"}
                                        labelPlacement="outside"
                                        variant="flat"
                                        isRequired={true}
                                        onBlur={(e) => {
                                            setBookPopup({...bookPopup, message: (e.target.value || '')});
                                        }}
                                    />
                                </div>}
                                <div className={"mt-4"}>
                                    <PInput placeholder=""
                                            isRequired={true}
                                            type="text"
                                            defaultValue={bookPopup.phone_number}
                                            onBlur={(e) => setBookPopup({...bookPopup, phone_number: (e.target.value || '')})}
                                            label="Your mobile number"
                                    />
                                </div>
                            </div>

                            {courtsQuery.data?.club?.booking_terms &&
                                <div className="p-4 pt-2">
                                    <div>
                                        <Checkbox isSelected={bookPopup.acceptedTerms}
                                                  isRequired={true}
                                                  onValueChange={(value) => setBookPopup({...bookPopup, acceptedTerms: value})}
                                                  className=""
                                        ></Checkbox> Agree to{' '}
                                        <a href={"#"} className="underline" onClick={(e) => {
                                            e.preventDefault();
                                            setBookPopup({...bookPopup, showTerms: !bookPopup.showTerms})
                                        }}>Booking Terms</a>
                                    </div>

                                    {bookPopup.showTerms && <Card className={"mt-2"}><CardBody>{courtsQuery.data?.club?.booking_terms}</CardBody></Card>}
                                </div>
                            }

                            <div className={"flex justify-center mb-4"}>
                                <PButton type={"submit"} color={"primary"} isLoading={bookCourt.isPending}>Submit</PButton>
                            </div>
                        </form>
                    )}
                </ModalContent>
            </Modal>

        </div>
    </>
}
